import React, { Fragment, useState, useEffect, useContext } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { JobAppContext } from '../../GlobalState'

import {
	updateJob,
	jobSettingValues,
	saveUserData
} from '../../api'

import { DateTime } from "luxon";
import {
	Alert, Button
} from 'reactstrap'

import { Checkbox, Input } from '../partials'
// import DateRange from '../partials/DateRange'
// import DateField from '../partials/DateField'
import FormDate from '../partials/FormDate'
import formatDate from '../../utils/formatDate'

import { ContactField } from '../partials/ContactField';
import { LocationField } from '../partials/LocationField';

import CurrencyField from '../Form/CurrencyField'
import useJobStatus from '../hooks/useJobStatus'
import { formatCurrencyValue, normalizeCurrencyValue } from '../../utils/currency'
import RequiredBadge from '../partials/RequiredBadge'
import {
	checkReferenceNoDuplicates,
	companyEnforcesUniqueReferenceNo,
} from '../../utils/checkReferenceNoDuplicates'


const JobSettings = (props) => {

	const { user, accessToken } = useContext(JobAppContext);

	const params = useParams();
	const history = useHistory();

	// const [ jobSettings, setJobSettings ] = useState({});

	const [ id, setId ] = useState(params.id);

	const [ saved, setSaved ] = useState(false);
	const [ isSaving, setIsSaving ] = useState(false);

	const [ isLoading, setIsLoading ] = useState(true);
	const [ isBlocking, setIsBlocking ] = useState(false);
	const [ readonly, setReadonly ] = useState(props?.readonly ?? true);

	const [ name, setName ] = useState('');
	const [ reference_no, setReferenceNo ] = useState('');
	const [ originalReferenceNo, setOriginalReferenceNo ] = useState('');
	const [ job_user, setJobUser ] = useState({});

	const [ contact, setContact ] = useState(0);
	const [ location, setLocation ] = useState(0);

	const [ first_furnishing, setFirstFurnishing ] = useState('');
	const [ ff_est, setFfEst ] = useState(0);
	const [ last_furnishing, setLastFurnishing ] = useState('');
	const [ lf_est, setLfEst ] = useState(0);

	const [ total_claim_amt, setTotalClaimAmt ] = useState('0.00');
	const [ total_contract_amt, setTotalContractAmt ] = useState('0.00');

	const [ user_submitted, setUserSubmitted ] = useState({});

	const [ prev_variant_id, setPrevVariantId ] = useState(0);
	const [ prev_location, setPrevLocation ] = useState('');

	const [ errorFound, setErrorFound ] = useState(false);
	const [ errorMsg, setErrorMsg ] = useState('');

	// Get job status from props or context
	const jobStatus = props?.status || 'open';
	
	// Use the job status hook
	const {
		statusConfig,
		isFieldEditable,
		shouldShowUserSubmitted,
		canPerformActions
	} = useJobStatus(jobStatus, readonly, !!user.api_key);

	// Get query client for cache invalidation
	const queryClient = useQueryClient()

	useEffect(() => {

		jobSettingValues(id).then(settings => {

			setName(settings.name);

			setReferenceNo(settings.reference_no);
			setOriginalReferenceNo(settings.reference_no);
			setJobUser(settings?.user ?? {
				id: 0, name: "Guest"
			});

			setLocation(settings.location ?? 0); // will trigger useEffect
			setContact(settings.contact ?? 0); // will trigger useEffect

			setFirstFurnishing(settings.first_furnishing);
			setFfEst(settings.ff_est);
			setLastFurnishing(settings.last_furnishing);
			setLfEst(settings.lf_est);

			setTotalClaimAmt(settings.total_claim_amt);
			setTotalContractAmt(settings.total_contract_amt);

			setUserSubmitted({
				...settings.user_submitted
			})

			setIsLoading(false);
			if ( settings.contact == 0 || settings.location == 0 ) {
				setErrorFound(true);
				setErrorMsg('Set the Contact and Location values below, to avoid error during Import')
			} else {
				setErrorFound(false);
				setErrorMsg('');
			}

		})

	}, [])

	/* ************************************************************************ */
	/* Field Change Actions                                                     */
	/* ************************************************************************ */

	// Only used for text fields on the Job's "Settings" tab
	const handleChange = () => {
		setIsBlocking(true);
		setSaved(false);
		setErrorFound(false);
		setErrorMsg('');
	}

	// when contact is changed, find the locations that they're setup for
	const changeContact = e => {
		const { value } = e.target
		setIsBlocking(true);
		if ( value == "0" ) {
			setContact("0");
		} else {
			setContact(value);
		}
	}

	const changeLocation = e => {
		const { value } = e.target;
		setIsBlocking(true);
		if ( value == "0" ) {
			setContact(0);
			setLocation(0);
		} else {
			setLocation(value);
		}
	}

	const handleDateChange = (field_id, value) => {
		console.log('handleDateChange')
		setIsBlocking(true);
		setSaved(false);

		if ( field_id === 'first_furnishing' ) {
			setFirstFurnishing(value);
		}
		if ( field_id === 'last_furnishing' ) {
			setLastFurnishing(value);
		}
	}

	const changeEstimated = e => {
		const { target } = e
		const { name, value, checked } = target
		
		setIsBlocking(true);
		setSaved(false);
		if ( name === 'ff_est' ) {
			setFfEst(checked ? 1 : 0)
		}
		if ( name === 'lf_est' ) {
			setLfEst(checked ? 1 : 0)
		}
	}

	/* ************************************************************************ */
	/* Save Actions                                                             */
	/* ************************************************************************ */

	const editSettings = () => {
		if (canPerformActions.edit) {
			setReadonly(false);
		}
	}

	const isDuplicateReferenceNo = async (nextReferenceNo) => {
		const nextRef = (nextReferenceNo || '').trim();
		const originalRef = (originalReferenceNo || '').trim();
		if (nextRef === '' || nextRef === originalRef) {
			return { duplicate: false, checkUnavailable: false };
		}

		const company = user?.memberships?.[0]?.company;
		const enforced = companyEnforcesUniqueReferenceNo(company);
		return checkReferenceNoDuplicates({
			referenceNo: nextRef,
			accessToken,
			company,
			enforced,
		});
	}

	const showDuplicateReferenceError = () => {
		setErrorFound(true);
		setErrorMsg(`${reference_label} already exists. Please use a unique value.`);
	}

	const showReferenceCheckUnavailableError = () => {
		setErrorFound(true);
		setErrorMsg('Could not verify reference number with OLS. Please try again later.');
	}

	const cancelEdit = () => {
		var answer = true
		if ( isBlocking ) {
			answer = confirm('Would you like to discard the current changes?')
		}
		if (answer) {
			// TODO: reset job setting form fields here
			if ( contact == 0 || location == 0 ) {
				setErrorFound(true);
			}
			setReadonly(true);
		}
		return answer
	}

	const saveSettings = async (e) => {
		if (!canPerformActions.save) {
			return;
		}

		setIsSaving(true);
		setErrorFound(false);
		setErrorMsg('');

		const data = {
			name               : name,
			selectedLocation   : location,
			selectedContact    : contact,
			reference_no       : reference_no,
			first_furnishing   : first_furnishing,
			ff_est             : ff_est,
			last_furnishing    : last_furnishing,
			lf_est             : lf_est,
			total_contract_amt : total_contract_amt,
			total_claim_amt    : total_claim_amt,
			// selectedWorkflow   : this.state.selectedWorkflow,
		}

		try {
			const refCheck = await isDuplicateReferenceNo(reference_no);
			if (refCheck.checkUnavailable) {
				showReferenceCheckUnavailableError();
				return;
			}
			if (refCheck.duplicate) {
				showDuplicateReferenceError();
				return;
			}

			const res = await updateJob(id, data);
			if (res?.error === 'reference_check_unavailable') {
				showReferenceCheckUnavailableError();
				return;
			}
			if (res?.error === 'duplicate_reference') {
				showDuplicateReferenceError();
				return;
			}
			// result == true
			if ( !res['result'] ) {
				setErrorFound(true);
				setErrorMsg('Error saving Job Import Settings. Please check field input/selections and try again');
			} else {
				// Invalidate jobs query cache to reflect updates
				queryClient.invalidateQueries({ queryKey: ['jobs', jobStatus] });
				
				// error saving update
				if ( data['selectedLocation'] == 0 || data['selectedContact'] == 0 ) {
					setReadonly(true);
					// setLogoUpdated(false);
					setErrorFound(true);
					setErrorMsg('Set the Contact and Location values below, to avoid error during Import');
				} else {
					setReadonly(true);
					// setLogoUpdated(false);
					setErrorFound(false);
					setErrorMsg('');
				}
				setOriginalReferenceNo(reference_no);
			}
		} catch (error) {
			setErrorFound(true);
			setErrorMsg('Error saving Job Import Settings. Please check field input/selections and try again');
		} finally {
			setIsSaving(false);
		}
	}

	const acceptUserValue = async (field, value) => {
		if (!canPerformActions.edit) {
			return;
		}

		const currentValues = {
			name,
			reference_no,
			first_furnishing,
			last_furnishing,
			total_claim_amt,
			total_contract_amt,
		};
		var old_value = currentValues[field];
		// Clean the value for currency fields by removing formatting
		let cleanValue = value;
		if (field === "total_claim_amt" || field === "total_contract_amt") {
			// Remove currency formatting for storage
			cleanValue = normalizeCurrencyValue(value);
		}

		try {
			if (field === "reference_no") {
				const refCheck = await isDuplicateReferenceNo(cleanValue);
				if (refCheck.checkUnavailable) {
					showReferenceCheckUnavailableError();
					return;
				}
				if (refCheck.duplicate) {
					showDuplicateReferenceError();
					return;
				}
			}
			
			switch(field) {
				case "name": setName(value); break;
				case "reference_no": setReferenceNo(value); break;
				case "first_furnishing": setFirstFurnishing(value); break;
				case "last_furnishing": setLastFurnishing(value); break;
				case "total_claim_amt": setTotalClaimAmt(cleanValue); break;
				case "total_contract_amt": setTotalContractAmt(cleanValue); break;
			}
			const data = await saveUserData(id, {'field': field, 'value': cleanValue});
			queryClient.invalidateQueries({ queryKey: ['jobs', jobStatus] });
			if ( data?.error === 'reference_check_unavailable' ) {
				showReferenceCheckUnavailableError();
				switch(field) {
					case "name": setName(old_value); break;
					case "reference_no": setReferenceNo(old_value); break;
					case "first_furnishing": setFirstFurnishing(old_value); break;
					case "last_furnishing": setLastFurnishing(old_value); break;
					case "total_claim_amt": setTotalClaimAmt(old_value); break;
					case "total_contract_amt": setTotalContractAmt(old_value); break;
				}
				return;
			}
			if ( data?.error === 'duplicate_reference' ) {
				showDuplicateReferenceError();
				switch(field) {
					case "name": setName(old_value); break;
					case "reference_no": setReferenceNo(old_value); break;
					case "first_furnishing": setFirstFurnishing(old_value); break;
					case "last_furnishing": setLastFurnishing(old_value); break;
					case "total_claim_amt": setTotalClaimAmt(old_value); break;
					case "total_contract_amt": setTotalContractAmt(old_value); break;
				}
				return;
			}
			if ( !data.success ) {
				switch(field) {
					case "name": setName(old_value); break;
					case "reference_no": setReferenceNo(old_value); break;
					case "first_furnishing": setFirstFurnishing(old_value); break;
					case "last_furnishing": setLastFurnishing(old_value); break;
					case "total_claim_amt": setTotalClaimAmt(old_value); break;
					case "total_contract_amt": setTotalContractAmt(old_value); break;
				}
			} else if ( field === "reference_no" ) {
				setOriginalReferenceNo(cleanValue);
			}
		} catch (error) {
			switch(field) {
				case "name": setName(old_value); break;
				case "reference_no": setReferenceNo(old_value); break;
				case "first_furnishing": setFirstFurnishing(old_value); break;
				case "last_furnishing": setLastFurnishing(old_value); break;
				case "total_claim_amt": setTotalClaimAmt(old_value); break;
				case "total_contract_amt": setTotalContractAmt(old_value); break;
			}
			setErrorFound(true);
			setErrorMsg('Error saving Job Import Settings. Please check field input/selections and try again');
		}
		
	}

	const company = user.memberships[0].company;
	const reference_label = company.reference_label != '' ? company.reference_label : 'Customer Reference #';

	return (
		<Fragment>

			{isLoading ? (
				<div><i className="fas fa-sync-alt fa-spin mr-1"></i> Loading Job Settings, Please wait...</div>
			) : (
				<>
					<div className={"card bg-light" + (jobStatus === 'pending' && !readonly ? ' border border-danger' : '')}>
						<div className="card-header d-flex">
							<h2 className="m-0">Client Information</h2>
							{canPerformActions.showEditButton && (
								<>
									{readonly ? (
										<i className="ml-3 far fa-edit" role="button" onClick={editSettings} />
									) : (
										<>
											<Button className="mx-2 btn-sm" color={isBlocking ? 'primary' : 'secondary'} onClick={saveSettings} disabled={isSaving || !isBlocking || !canPerformActions.save}>
												{isSaving ? "Saving" : "Save"}
											</Button>
											<Button className="btn-sm" onClick={cancelEdit} disabled={isSaving}>
												Cancel
											</Button>
										</>
									)}	
								</>
							)}
						</div>
						<div className="card-body">
							{errorFound && (
								<Alert color="danger">
									<i className="fas fa-exclamation-triangle mr-3"></i>
									{errorMsg}
								</Alert>
							)}

							<div>
								<div className="form-group">
									<LocationField 
										value={location} 
										change={changeLocation} 
										readOnly={!isFieldEditable()} 
									/>
								</div>
								<div className="form-group">
									<ContactField 
										value={contact} 
										change={changeContact} 
										divId={location}
										readOnly={!isFieldEditable()} 
									/>
								</div>
							</div>
							
							<div className="row">
								
								<div className="col">
									
									<div className="form-group">
										<div className="form-row">
											<div className="col">
												<Input
													name="name"
													label="Job Name"
													value={name}
													onChange={(e) => { handleChange(); setName(e.target.value) }}
													readOnly={!isFieldEditable()}
													className=" inline"
												/>
												{shouldShowUserSubmitted(user_submitted.project_name, name) && (
													<span className="userValue ml-3" title="Accept User value" onClick={() => acceptUserValue('name', user_submitted.project_name)}>
														{user_submitted.project_name}
														<i className="fas fa-check ml-1"></i>
													</span>
												)}
											</div>
										</div>
									</div>

									<div className="form-group">
										<div className="form-row">
											<div className="col">
												<Input 
													name="reference_no"
													label={reference_label}
													value={reference_no}
													onChange={(e) => { handleChange(); setReferenceNo(e.target.value) }}
													readOnly={!isFieldEditable()}
												/>
												{shouldShowUserSubmitted(user_submitted.reference_no, reference_no) && (
													<span className="userValue ml-3" title="Accept User value" onClick={() => acceptUserValue('reference_no', user_submitted.reference_no)}>
														{user_submitted.reference_no}
														<i className="fas fa-check ml-1"></i>
													</span>
												)}
											</div>
										</div>
									</div>

									{readonly ? (
										<div className="form-group">
											<div className="form-row">
												<div className="col-4">
													<label>First Furnishing</label>
													<span>{formatDate(first_furnishing) + (ff_est == 1 ? ' (estimated)' : '')}</span>
													{shouldShowUserSubmitted(user_submitted.first_furnishing, first_furnishing) && (
														<span className="userValue ml-3" title="Accept User value" onClick={() => acceptUserValue('first_furnishing', user_submitted.first_furnishing)}>
															{formatDate(user_submitted.first_furnishing)}
															<i className="fas fa-check ml-1"></i>
														</span>
													)}
												</div>
												<div className="col">
													<label>Last Furnishing</label>
													<span>{formatDate(last_furnishing) + (lf_est == 1 ? ' (estimated)' : '')}</span>
													{shouldShowUserSubmitted(user_submitted.last_furnishing, last_furnishing) && (
														<span className="userValue ml-3" title="Accept User value" onClick={() => acceptUserValue('last_furnishing', user_submitted.last_furnishing)}>
															{formatDate(user_submitted.last_furnishing)}
															<i className="fas fa-check ml-1"></i>
														</span>
													)}
												</div>
											</div>
										</div>
									) : (
										<div className="form-group">
											<div className="form-row">
												<div className={'col-4'}>
													<div className={'form-group'}>
														{/* <DateField
															name={"from"}  
															label={"First Furnishing"}
															field_id={"first_furnishing"}
															onChangeDate={handleDateChange}
															isRequired={true}
															value={formatDate(first_furnishing) ?? ""}
														/> */}
														<label htmlFor="first_furnishing">
															First Furnishing
															<RequiredBadge value={first_furnishing} />{' '}
														</label>
														<FormDate
															light
															value={first_furnishing}
															onChange={(date) => handleDateChange('first_furnishing', formatDate(date))}
															placeholder="MM/DD/YYYY"
														/>
													</div>
												</div>
												<div className={'col'}>
													<div className={'form-group'}>
														{/* <DateField
															name={"to"}  
															label={"Last Furnishing"}
															field_id={"last_furnishing"}
															onChangeDate={handleDateChange}
															isRequired={true}
															value={formatDate(last_furnishing) ?? ""}
														/> */}
														<label htmlFor="last_furnishing">
															Last Furnishing
															<RequiredBadge value={last_furnishing} />{' '}
														</label>
														<FormDate
															light
															value={last_furnishing}
															onChange={(date) => handleDateChange('last_furnishing', formatDate(date))}
															placeholder="MM/DD/YYYY"
														/>
													</div>
												</div>
												{/*
												<DateRange
													start={formatDate(first_furnishing) ?? ""}
													end={formatDate(last_furnishing) ?? ""}
													onChangeFrom={(value) => setJobSettings({ ...jobSettings, first_furnishing: value })}
													onChangeTo={(value) => setJobSettings({ ...jobSettings, last_furnishing: value })}
												/>
												*/}
											</div>
											<div className="form-row">
												<div className="col-4">
													<div style={{ marginLeft: '10px' }}>
														<Checkbox
															onChange={changeEstimated}
															label="Check if estimated"
															name="ff_est"
															value={ff_est == 1 ? true : false}
														/>
													</div>
												</div>
												<div className="col">
													<div style={{ marginLeft: '10px' }}>
														<Checkbox
															onChange={changeEstimated}
															label="Check if estimated"
															name="lf_est"
															value={lf_est == 1 ? true : false}
														/>
													</div>
												</div>
											</div>
										</div>
									)}
									
									<div className="form-group">
										<div className="form-row">
											<div className="col-4">


												<CurrencyField
													label={"Total Contract Amount"}
													name={"total_contract_amt"}
													value={total_contract_amt}
													onChange={(e) => { handleChange(); setTotalContractAmt(e.target.value); }}
													readOnly={!isFieldEditable()}
													// pattern="^\d*(\.\d{0,2})?$"
												/>

												{shouldShowUserSubmitted(user_submitted?.total_contract_amt, total_contract_amt) && (
													<span className="userValue ml-3" title="Accept User value" onClick={() => acceptUserValue('total_contract_amt', user_submitted.total_contract_amt)}>
														{formatCurrencyValue(user_submitted.total_contract_amt)}
														<i className="fas fa-check ml-1"></i>
													</span>
												)}
											</div>
											
											<div className="col">

												<CurrencyField
													label={"Total Claim Amount"}
													name={"total_claim_amt"}
													value={total_claim_amt}
													onChange={(e) => { handleChange(); setTotalClaimAmt(e.target.value); }}
													readOnly={!isFieldEditable()}
													// pattern="^\d*(\.\d{0,2})?$"
												/>

												{shouldShowUserSubmitted(user_submitted?.total_claim_amt, total_claim_amt) && (
													<span className="userValue ml-3" title="Accept User value" onClick={() => acceptUserValue('total_claim_amt', user_submitted.total_claim_amt)}>
														{formatCurrencyValue(user_submitted.total_claim_amt)}
														<i className="fas fa-check ml-1"></i>
													</span>
												)}
											</div>
										</div>
									</div>

								</div>
							</div>
						</div>
					</div>
				</>		
			)}
		</Fragment>
	)
}
export default JobSettings;
